import { HitData } from 'common/store/common/search/types';
import i18nService, { ContentLanguagesDefinition } from 'common/services/i18nService';
import { ContentTypes } from 'common/store/common/constants';
import { ActionTypes } from './actions';
import {
    AdditionalResource,
    AlternativesLimitErrorMsg, ContentEditStatus,
    ContextTypeKeys,
    ItemBankInfo,
    ItemContext,
    QTIContextItem,
} from './types';
import { Assessment } from '../../features/questionDelivery/types';
import {
    getActiveContentLanguage, getEditedQuestionId, getPersistedContentLanguage, getPersistedQuestionContentLanguage,
    getQuestionSetLanguages, isAllowedInQuestionSet, persistContentLanguage,
} from './questionContentLanguage';

export type ContextState = {
    readonly contentItemIds: number[] | number;
    readonly contentItems: HitData[];
    readonly contextId: number;
    readonly currentItemContext: ItemContext;
    readonly currentQtiContextItem: QTIContextItem;
    readonly currentQtcSectionData?: any;
    readonly currentResourceContextItem: AdditionalResource;
    readonly isDirtyCurrentQtiContextItem: boolean;
    readonly itemBankId: number;
    readonly itemBankInfo: ItemBankInfo;
    readonly qtiContextItemId: number;
    readonly resourceContextItemId: number;
    readonly questionId?: number;
    readonly scorableItem?: number;
    readonly sectionId?: number;
    readonly workspaceId: string;
    readonly navigationLock: boolean;
    readonly isLocked: boolean;
    contentEditStatus?: ContentEditStatus;
    readonly itemBankForbidden?: boolean;
    readonly [key: string]: any;
    readonly contentTemplate: string;
    readonly isIBTemplate: boolean;
    readonly dakKeyEvent?: DynamicAnswerKeyEvents;
    readonly isQTIContextLocked?: boolean | null;
    readonly maxAnswersWarningMessage?: AlternativesLimitErrorMsg;
    readonly assessmentData?: Assessment;
    readonly assessmentLoading?: boolean;
    readonly updateLoading?: boolean;
    readonly bulkUpdateSuccessCount?: number;
    readonly showBulkUpdateSuccessNotification?: boolean;
    readonly activeLanguage?: ContentLanguagesDefinition;
    readonly activeContentLanguage?: ContentLanguagesDefinition;
    readonly questionLanguageUpdateInProgress?: boolean;
};

export enum DynamicAnswerKeyEventsEnum {
    ADD_ANSWER_KEY_CONTENT = 'add_answer_key_content',
    EDIT_ANSWER_KEY_CONTENT = 'edit_answer_key_content',
    DELETE_ANSWER_KEY_CONTENT = 'delete_answer_key_content',
    UPDATE_ANSWER_KEY_CONTENT = 'update_answer_key_content',
}

export type DynamicAnswerKeyEvents = DynamicAnswerKeyEventsEnum;

export type EditQuestionInContextReq = {
    textContent: string;
    contentItemId: number;
    contextContentItemId?: number;
    event?: DynamicAnswerKeyEventsEnum;
}

export const initialState: ContextState = {
    contentItemIds: [],
    contentItems: [],
    contextId: -1,
    currentItemContext: null,
    currentQtiContextItem: null,
    currentQtcSectionData: null,
    currentResourceContextItem: null,
    isDirtyCurrentQtiContextItem: false,
    itemBankId: -1,
    itemBankInfo: null,
    qtiContextItemId: -1,
    resourceContextItemId: -1,
    questionId: -1,
    scorableItem: -1,
    sectionId: -1,
    workspaceId: null,
    navigationLock: false,
    showPropertiesPanel: false,
    isLocked: false,
    contentEditStatus: undefined,
    showContextLockModal: false,
    itemBankForbidden: false,
    contentTemplate: null,
    isIBTemplate: false,
    isQTIContextLocked: null,
    maxAnswersWarningMessage: undefined,
    assessmentData: null,
    assessmentLoading: false,
    updateSuccess: false,
    assessmentDetails: {},
    bulkUpdateSuccessCount: undefined,
    showBulkUpdateSuccessNotification: false,
    activeLanguage: undefined,
    activeContentLanguage: undefined,
};

export const contextReducer = (state: ContextState = initialState, action: ActionTypes): ContextState => {
    switch (action.type) {
    case ContextTypeKeys.SET_WORKSPACE_ID:
        return {
            ...state,
            workspaceId: action.workspaceId,
        };
    case ContextTypeKeys.SET_DYNAMIC_ANSWER_KEY_EVENT:
        return {
            ...state,
            dakKeyEvent: action.dakKeyEvent,
        };
    case ContextTypeKeys.SET_ITEM_BANK_ID:
        return {
            ...state,
            itemBankId: action.itemBankId,
        };
    case ContextTypeKeys.SET_CONTENT_ITEMS:
        return {
            ...state,
            contentItems: action.contentItems,
        };
    case ContextTypeKeys.SET_CONTENT_ITEM_IDS:
        return {
            ...state,
            contentItemIds: action.contentItemIds,
        };
    case ContextTypeKeys.SET_CURRENT_ITEM_CONTEXT: {
        const isSameItem = state.currentItemContext?.objectId === action.currentItemContext?.objectId;
        const existingSupportedLanguages = state.currentItemContext?.supportedLanguages || [];
        const fetchedSupportedLanguages = action.currentItemContext?.supportedLanguages || [];
        const shouldKeepExistingLanguages = isSameItem && existingSupportedLanguages.length > fetchedSupportedLanguages.length;
        return {
            ...state,
            currentItemContext: shouldKeepExistingLanguages
                ? { ...action.currentItemContext, supportedLanguages: existingSupportedLanguages }
                : action.currentItemContext,
            contextId: action.contextId,
        };
    }
    case ContextTypeKeys.SET_TEMPLATE_IN_CURRENT_ITEM_CONTEXT:
        return {
            ...state,
            currentItemContext: { ...state.currentItemContext, isTemplate: action.isTemplate },
        };
    case ContextTypeKeys.SET_CURRENT_ITEM_CONTEXT_LOCK:
        return {
            ...state,
            isLocked: action.isLocked,
        };
    case ContextTypeKeys.SET_CURRENT_QTI_CONTEXT_ITEM: {
        const itemChanged = action.qtiContextItemId !== state.qtiContextItemId;
        // When switching to a different question, use only the language persisted
        // for that question. Metadata will choose the question default when there
        // is no persisted selection.
        //
        // NOTE: at this point supportedLanguages may not yet be populated on
        // the index hit (it arrives later via SET_CURRENT_QTI_CONTEXT_ITEM_META_DATA).
        // A language from the prior question must never be carried forward while
        // the new question's metadata is loading.
        const newSupported = (action.currentQtiContextItem?.supportedLanguages || []) as string[];
        const nextActiveLang = getPersistedQuestionContentLanguage(
            action.currentQtiContextItem?.objectId,
            getQuestionSetLanguages(state.currentItemContext),
            newSupported,
            itemChanged,
        );
        return {
            ...state,
            currentQtiContextItem: action.currentQtiContextItem,
            qtiContextItemId: action.qtiContextItemId,
            ...(itemChanged && { activeContentLanguage: nextActiveLang }),
        };
    }
    case ContextTypeKeys.SET_CURRENT_RESOURCE_CONTEXT_ITEM:
        return {
            ...state,
            currentResourceContextItem: action.payload,
            resourceContextItemId: action.payload?.contentItemId,
        };
    case ContextTypeKeys.SET_ITEM_BANK_INFO:
        return {
            ...state,
            itemBankInfo: action.itemBankInfo,
        };
    case ContextTypeKeys.SET_CONTEXT_ITEM_BANK_INFO:
        return {
            ...state,
            currentQtiContextItem: {
                ...state.currentQtiContextItem,
                itemBankInfo: action.itemBankInfo,
            },
        };
    case ContextTypeKeys.SET_CONTEXT_EDIT_STATUS:
        return {
            ...state,
            contentEditStatus: action.contentEditStatus,
        };
    case ContextTypeKeys.RESET_CONTEXT_EDIT_STATUS:
        return {
            ...state,
            contentEditStatus: undefined,
        };
    case ContextTypeKeys.SHOW_CONTEXT_LOCK_MODAL:
        return {
            ...state,
            showContextLockModal: true,
        };
    case ContextTypeKeys.CLOSE_CONTEXT_LOCK_MODAL:
        return {
            ...state,
            showContextLockModal: false,
        };
    case ContextTypeKeys.SET_QUESTION_ID_AND_SCORABLE_ITEM_AND_SECTION_ID:
        return {
            ...state,
            questionId: action.questionId,
            scorableItem: action.scorableItem,
            sectionId: action.sectionId,
        };
    case ContextTypeKeys.RESET_QUESTION_ID_AND_SCOREABLE_ITEM_AND_SECTION_ID:
        return {
            ...state,
            questionId: -1,
            scorableItem: -1,
            sectionId: -1,
        };
    case ContextTypeKeys.TOGGLE_NAVIGATION_LOCK:
        return {
            ...state,
            navigationLock: action.payload,
        };
    case ContextTypeKeys.TOGGLE_PROPERTIES_PANEL:
        return {
            ...state,
            showPropertiesPanel: !state.showPropertiesPanel,
        };
    case ContextTypeKeys.SET_CURRENT_ITEM_CONTEXT_DIRTY:
        return {
            ...state,
            isDirtyCurrentQtiContextItem: action.isDirty,
        };
    case ContextTypeKeys.SET_IS_IB_TEMPLATE:
        return {
            ...state,
            isIBTemplate: action.isIBTemplate,
        };
    case ContextTypeKeys.SET_CURRENT_QTI_CONTEXT_ITEM_META_DATA:
    {
        const newState = {
            ...state,
            questionMetaData: action.questionMetaData,
            currentQtiContextItem: {
                ...state.currentQtiContextItem,
            },
            currentItemContext: {
                ...state.currentItemContext,
            },
        };
        if (action.questionMetaData.publishStatus) {
            newState.currentQtiContextItem.publishStatus = action.questionMetaData.publishStatus;
        }
        if (action.questionMetaData.liveContentRevisionId) {
            newState.currentQtiContextItem.contentRevisionId = action.questionMetaData.liveContentRevisionId;
        }
        if (action.questionMetaData.defaultLanguage) {
            newState.currentQtiContextItem.defaultLanguage = action.questionMetaData.defaultLanguage;
        }
        if (action.questionMetaData.supportedLanguages) {
            // Do NOT sync to currentItemContext — question-set and question
            // languages are decoupled. currentItemContext.supportedLanguages
            // is managed by SET_QUESTION_SET_SUPPORTED_LANGUAGES (HeaderLeft).
            const rawLangs = action.questionMetaData.supportedLanguages as string[];
            const questionSetLangs = (state.currentItemContext?.supportedLanguages || []) as string[];
            const belongsToRealQuestionSet = state.currentItemContext?.contentType === ContentTypes.QUESTION_SET;
            const normalizedRawLangs = rawLangs.map((l) => i18nService.normalizeToContentLanguage(l));
            const normalizedQsLangs = new Set(questionSetLangs.map((l) => i18nService.normalizeToContentLanguage(l)));
            const hasOverlap = normalizedRawLangs.some((l) => normalizedQsLangs.has(l));
            const qsLangToAdd = state.activeLanguage && normalizedQsLangs.has(
                i18nService.normalizeToContentLanguage(state.activeLanguage)
            ) ? state.activeLanguage : questionSetLangs[0];

            newState.currentQtiContextItem.supportedLanguages = belongsToRealQuestionSet
                && !hasOverlap && questionSetLangs.length > 0 && qsLangToAdd
                ? Array.from(new Set([...rawLangs, qsLangToAdd]))
                : rawLangs;

            // Only set activeContentLanguage if it's not already set or if the
            // current value is not valid for the updated languages. This prevents
            // overwriting the user's selected tab when metadata is refreshed
            // after a language-save operation.
            const defaultLang = action.questionMetaData.defaultLanguage;
            const langs = (newState.currentQtiContextItem.supportedLanguages as string[])
                .filter((language) => isAllowedInQuestionSet(language, belongsToRealQuestionSet ? questionSetLangs : []));
            const currentActive = state.activeContentLanguage;
            const contentItemId = getEditedQuestionId(newState.currentQtiContextItem, state.contextId);
            const persisted = getPersistedContentLanguage(contentItemId, langs);
            newState.activeContentLanguage = getActiveContentLanguage(
                currentActive,
                persisted,
                defaultLang,
                langs,
                state.activeLanguage
            );
            if (newState.activeContentLanguage) {
                persistContentLanguage(newState.activeContentLanguage, contentItemId);
            }
        }
        return newState;
    }
    case ContextTypeKeys.SET_IS_QTI_CONTEXT_LOCKED:
        return {
            ...state,
            isQTIContextLocked: action.isQTIContextLocked,
        };
    case ContextTypeKeys.SET_MAX_ANSWERS_WARNING:
        return {
            ...state,
            maxAnswersWarningMessage: action.maxAnswersWarningMessage,
        };
    case ContextTypeKeys.SET_CONTEXT_QTI_ITEM_PUBLISH_STATUS:
        return {
            ...state,
            currentQtiContextItem: {
                ...state.currentQtiContextItem,
                publishStatus: action.publishStatus,
            },
        };
    case ContextTypeKeys.SET_CONTEXT_QTI_ITEM_BANK_ID:
        return {
            ...state,
            currentQtiContextItem: {
                ...state.currentQtiContextItem,
                itemBankId: action.itemBankId,
            },
        };
    case ContextTypeKeys.SET_CURRENT_QTC_SECTION_DATA:
        return {
            ...state,
            currentQtcSectionData: action.sectionData,
        };
    case ContextTypeKeys.SET_ITEM_BANK_FORBIDDEN:
        return {
            ...state,
            itemBankForbidden: action.isForbidden,
        };
    case ContextTypeKeys.GET_ASSESSMENT:
    case ContextTypeKeys.GET_ASSESSMENT_DETAILS:
        return {
            ...state,
            assessmentLoading: true,
        };
    case ContextTypeKeys.GET_ASSESSMENT_SUCCESS:
        return {
            ...state,
            assessmentLoading: false,
            assessmentData: action.payload,
        };   
    case ContextTypeKeys.GET_ASSESSMENT_DETAILS_SUCCESS:
        return {
            ...state,
            assessmentLoading: false,
            assessmentDetails: action.payload,
        };
    case ContextTypeKeys.UPDATE_ASSESSMENT:
    case ContextTypeKeys.ADD_CONTENT_BLOCK_SELECTION:
        return {
            ...state,
            updateLoading: true,
        };
    case ContextTypeKeys.UPDATE_ASSESSMENT_SUCCESS:
    case ContextTypeKeys.ADD_CONTENT_BLOCK_SELECTION_SUCCESS:
        return {
            ...state,
            updateLoading: false,
        };
    case ContextTypeKeys.BULK_UPDATE_ASSESSMENT:
        return {
            ...state,
            updateLoading: true,
            bulkUpdateSuccessCount: undefined,
            showBulkUpdateSuccessNotification: false,
        };
    case ContextTypeKeys.SHOW_BULK_UPDATE_SUCCESS_NOTIFICATION:
        return {
            ...state,
            updateLoading: false,
            bulkUpdateSuccessCount: action.count,
            showBulkUpdateSuccessNotification: true,
        };
    case ContextTypeKeys.CLEAR_BULK_UPDATE_SUCCESS_NOTIFICATION:
        return {
            ...state,
            showBulkUpdateSuccessNotification: false,
        };
    case ContextTypeKeys.VIEW_TYPE:
        return {
            ...state,
            viewType: action.payload,
        };
    case ContextTypeKeys.SET_ACTIVE_LANGUAGE:
        return {
            ...state,
            activeLanguage: action.activeLanguage,
        };
    case ContextTypeKeys.SET_QUESTION_SUPPORTED_LANGUAGES:
        return {
            ...state,
            currentQtiContextItem: {
                ...state.currentQtiContextItem,
                supportedLanguages: action.supportedLanguages,
            },
            questionLanguageUpdateInProgress: true,
        };
    case ContextTypeKeys.QUESTION_LANGUAGE_UPDATE_COMPLETE:
        return {
            ...state,
            questionLanguageUpdateInProgress: false,
        };
    case ContextTypeKeys.SET_QUESTION_SET_SUPPORTED_LANGUAGES:
        return {
            ...state,
            currentItemContext: {
                ...state.currentItemContext,
                supportedLanguages: action.supportedLanguages,
            },
        };
    case ContextTypeKeys.SET_ACTIVE_CONTENT_LANGUAGE: {
        // Only persist when the dispatcher asked to (default true). Auto-init
        // dispatches (e.g. ContentEditorHeader picking a default when no
        // active language is set) pass persist=false so they don't overwrite
        // the user's previously persisted per-question selection.
        const normalizedActiveLanguage = i18nService.normalizeToContentLanguage(action.activeContentLanguage);
        if (action.persist !== false) {
            persistContentLanguage(
                normalizedActiveLanguage,
                getEditedQuestionId(state.currentQtiContextItem, state.contextId)
            );
        }
        return {
            ...state,
            activeContentLanguage: normalizedActiveLanguage,
        };
    }
    default:
        return state;
    }
};
